/**
 * Promo Form Hook
 * 
 * Centralized form state management for promo creation/editing:
 * - Form data state and validation
 * - Step navigation and validation
 * - Draft saving and promo submission
 * - API error handling
 */

import { useState, useCallback, useMemo, useEffect } from 'react';
import { 
  PromoFormData, 
  PromoValidationErrors, 
  PromoStepConfig,
  Domain,
  TransactionType,
  UserType,
  BenefitType,
  BenefitLevel,
  DiscountMode,
  EntityTargetingMode,
  PromoStatus,
} from '../../../../../api/promos/types';
import { createPromo, savePromoAsDraft, updatePromo } from '../../../../../api/promos';

interface UsePromoFormReturn {
  // State
  currentStep: number;
  formData: PromoFormData;
  validationErrors: PromoValidationErrors;
  isDraft: boolean;
  isSubmitting: boolean;
  steps: PromoStepConfig[];
  
  // Navigation
  goToStep: (stepIndex: number) => void;
  validateStep: (stepIndex: number) => boolean;
  
  // Form management
  handleFormChange: (updates: Partial<PromoFormData>) => void;
  
  // Submission
  saveDraft: () => Promise<void>;
  submitPromo: () => Promise<void>;
}

export const usePromoForm = (
  initialData: PromoFormData,
  promoId?: string,
  isEditMode: boolean = false
): UsePromoFormReturn => {
  // Form state
  const [currentStep, setCurrentStep] = useState(0);
  const [formData, setFormData] = useState<PromoFormData>(initialData);
  const [validationErrors, setValidationErrors] = useState<PromoValidationErrors>({});
  const [isDraft, setIsDraft] = useState(false);
  const [isSubmitting, setIsSubmitting] = useState(false);
  
  // Update formData when initialData changes (for edit mode)
  useEffect(() => {
    setFormData(initialData);
  }, [initialData]);
  
  // Validation functions
  const validateStepData = useCallback((stepIndex: number): boolean => {
    const errors: PromoValidationErrors = {};
    
    switch (stepIndex) {
      case 0: // Basic Information
        if (!formData.name?.trim()) {
          errors.name = 'Promo name is required';
        } else if (formData.name.trim().length < 3) {
          errors.name = 'Promo name must be at least 3 characters';
        }
        
        // Code is optional for AUTOMATIC mode, required for CODE_BASED
        if (formData.discountMode === DiscountMode.CODE_BASED && !formData.code?.trim()) {
          errors.code = 'Promo code is required for code-based promos';
        }
        
        if (!formData.domain) {
          errors.domain = 'Domain is required';
        }
        
        if (!formData.transactionType) {
          errors.transactionType = 'Transaction type is required';
        }
        
        break;
        
      case 1: // Discount Configuration
        if (!formData.benefit?.type) {
          if (!errors.benefit) errors.benefit = {};
          errors.benefit.type = 'Discount type is required';
        }
        
        if (!formData.benefit?.level) {
          if (!errors.benefit) errors.benefit = {};
          errors.benefit.level = 'Discount level is required';
        }
        
        if (formData.benefit?.value === undefined || formData.benefit?.value === null || formData.benefit?.value <= 0) {
          if (!errors.benefit) errors.benefit = {};
          errors.benefit.value = 'Discount value is required and must be greater than 0';
        }

        if (!formData.listingTitle?.trim()) {
          errors.listingTitle = 'Listing title is required';
        }

        if (!formData.appliedTitle?.trim()) {
          errors.appliedTitle = 'Applied title is required';
        }
        
        // Max discount cap is optional - no validation required
        
        break;
        
      case 2: // Targeting
        // Validate entity targeting: if mode is INCLUDE or EXCLUDE, entityIds are required
        if (formData.scope?.entityTargeting) {
          const mode = formData.scope.entityTargeting.mode;
          if ((mode === EntityTargetingMode.INCLUDE || mode === EntityTargetingMode.EXCLUDE) &&
              (!formData.scope.entityTargeting.entityIds || formData.scope.entityTargeting.entityIds.length === 0)) {
            if (!errors.scope) errors.scope = {};
            errors.scope.entityTargeting = 'Please select at least one entity';
          }
        }
        
        // Validate filters: if a filter has a field, it must have at least one value
        if (formData.scope?.filters && formData.scope.filters.length > 0) {
          const invalidFilters = formData.scope.filters.filter(filter => {
            // If filter has a field, it must have values
            if (filter.field && filter.field.trim() !== '') {
              const values = Array.isArray(filter.value) ? filter.value : [];
              return values.length === 0;
            }
            return false;
          });
          
          if (invalidFilters.length > 0) {
            if (!errors.scope) errors.scope = {};
            errors.scope.filters = 'Please select at least one value for each filter';
          }
        }
        break;
        
      case 3: // Eligibility and Conditions
        if (!formData.eligibility?.userType) {
          if (!errors.eligibility) errors.eligibility = {};
          errors.eligibility.userType = 'User eligibility is required';
        }
        
        // Minimum Order Value is mandatory
        if (!formData.conditions?.minOrderAmount || formData.conditions.minOrderAmount <= 0) {
          if (!errors.conditions) errors.conditions = {};
          errors.conditions.minOrderAmount = 'Minimum Order Value is required and must be greater than 0';
        }
        
        // Usage Limits validation - all fields are mandatory
        if (!formData.usageLimits?.totalLimit || formData.usageLimits.totalLimit <= 0) {
          if (!errors.usageLimits) errors.usageLimits = {};
          errors.usageLimits.totalLimit = 'Total Usage Limit is required and must be greater than 0';
        }
        
        if (!formData.usageLimits?.perUserLimit || formData.usageLimits.perUserLimit <= 0) {
          if (!errors.usageLimits) errors.usageLimits = {};
          errors.usageLimits.perUserLimit = 'Per User Limit is required and must be greater than 0';
        }
        
        // Valid From Date is mandatory
        if (!formData.validity?.startAt) {
          if (!errors.validity) errors.validity = {};
          errors.validity.startAt = 'Valid From Date is required';
        }
        
        // Expiry Date is mandatory
        if (!formData.validity?.endAt) {
          if (!errors.validity) errors.validity = {};
          errors.validity.endAt = 'Expiry Date is required';
        }
        
        // Date validation - validate end date is after start date
        if (formData.validity?.startAt && formData.validity?.endAt) {
          if (new Date(formData.validity.endAt) <= new Date(formData.validity.startAt)) {
            if (!errors.validity) errors.validity = {};
            errors.validity.endAt = 'Expiry date must be after valid from date';
          }
        }
        
        break;
        
      case 4: // Stackability Rules
        // Max Total Discount % Cap is mandatory
        if (!formData.stackingRules?.maxTotalDiscountPercent || formData.stackingRules.maxTotalDiscountPercent <= 0) {
          if (!errors.stackingRules) errors.stackingRules = {};
          errors.stackingRules.maxTotalDiscountPercent = 'Max Total Discount % Cap is required and must be greater than 0';
        }
        break;
        
      case 5: // Summary
        // Final validation - all previous steps must be valid
        const step0Valid = validateStepData(0);
        const step1Valid = validateStepData(1);
        const step3Valid = validateStepData(3);
        return step0Valid && step1Valid && step3Valid;
    }
    
    setValidationErrors(errors);
    return Object.keys(errors).length === 0;
  }, [formData]);

  // Step configuration
  const steps: PromoStepConfig[] = useMemo(() => [
    {
      id: 'basic-info',
      title: 'Basic Information',
      isCompleted: currentStep > 0 && validateStepData(0),
    },
    {
      id: 'discount-config',
      title: 'Discount Config',
      isCompleted: currentStep > 1 && validateStepData(1),
    },
    {
      id: 'targeting',
      title: 'Targeting',
      isCompleted: currentStep > 2,
    },
    {
      id: 'eligibility',
      title: 'Eligibility & Conditions',
      isCompleted: currentStep > 3 && validateStepData(3),
    },
    {
      id: 'stackability',
      title: 'Stackability Rules',
      isCompleted: currentStep > 4,
    },
    {
      id: 'summary',
      title: 'Summary',
      isCompleted: currentStep > 5,
    },
  ], [currentStep, validateStepData]);
  
  // Navigation functions
  const goToStep = useCallback((stepIndex: number) => {
    if (stepIndex >= 0 && stepIndex < steps.length) {
      setCurrentStep(stepIndex);
      // Clear validation errors when changing steps
      setValidationErrors({});
    }
  }, [steps.length]);
  
  const validateStep = useCallback((stepIndex: number) => {
    return validateStepData(stepIndex);
  }, [validateStepData]);
  
  // Form management
  const handleFormChange = useCallback((updates: Partial<PromoFormData>) => {
    setFormData(prev => ({ ...prev, ...updates }));
    // Clear related validation errors
    if (updates.name) {
      setValidationErrors(prev => ({ ...prev, name: undefined }));
    }
    if (updates.code) {
      setValidationErrors(prev => ({ ...prev, code: undefined }));
    }
    if (updates.domain) {
      setValidationErrors(prev => ({ ...prev, domain: undefined }));
    }
    if (updates.transactionType) {
      setValidationErrors(prev => ({ ...prev, transactionType: undefined }));
    }
    if (updates.benefit) {
      setValidationErrors(prev => ({ ...prev, benefit: undefined }));
    }
    if (updates.listingTitle !== undefined) {
      setValidationErrors(prev => ({ ...prev, listingTitle: undefined }));
    }
    if (updates.appliedTitle !== undefined) {
      setValidationErrors(prev => ({ ...prev, appliedTitle: undefined }));
    }
    if (updates.eligibility) {
      setValidationErrors(prev => ({ ...prev, eligibility: undefined }));
    }
    if (updates.validity) {
      setValidationErrors(prev => ({ ...prev, validity: undefined }));
    }
  }, []);
  
  // Save as draft
  const saveDraft = useCallback(async () => {
    try {
      setIsSubmitting(true);
      setIsDraft(true);
      
      // Basic validation for draft (only required fields from step 1)
      if (!formData.name?.trim()) {
        throw new Error('Promo name is required to save as draft');
      }
      
      if (isEditMode && promoId) {
        // Update existing promo
        await updatePromo(promoId, formData);
      } else {
        // Create new promo with DRAFT status
        await savePromoAsDraft(formData);
      }
    } catch (error) {
      throw error;
    } finally {
      setIsSubmitting(false);
    }
  }, [formData, isEditMode, promoId]);
  
  // Submit promo (activate)
  const submitPromo = useCallback(async () => {
    try {
      setIsSubmitting(true);
      setIsDraft(false);
      
      // Validate all required steps
      const allStepsValid = validateStepData(0) && validateStepData(1) && validateStepData(3);
      
      if (!allStepsValid) {
        throw new Error('Please complete all required fields before activating');
      }
      
      if (isEditMode && promoId) {
        // Update existing promo
        await updatePromo(promoId, formData);
      } else {
        // Create new promo with ACTIVE status
        await createPromo(formData, PromoStatus.ACTIVE);
      }
    } catch (error) {
      throw error;
    } finally {
      setIsSubmitting(false);
    }
  }, [formData, validateStepData, isEditMode, promoId]);
  
  return {
    // State
    currentStep,
    formData,
    validationErrors,
    isDraft,
    isSubmitting,
    steps,
    
    // Navigation
    goToStep,
    validateStep,
    
    // Form management
    handleFormChange,
    
    // Submission
    saveDraft,
    submitPromo,
  };
};
